import "@/lib/i18n"; // This will initialize i18n on client side
import { ReactNode, useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useUserOptional } from "@/components/auth-provider";

interface I18nProviderProps {
  children: ReactNode;
}

// Make the server-persisted `user.language` authoritative for the UI language
// on load. Without this, the language lived only in browser localStorage, so a
// user's choice never followed them to a new device and — more importantly —
// could drift from the value the AI assistant reads (`user.language`). Applying
// the stored preference here keeps the UI, the persisted record, and the AI's
// "Response language" in sync. Explicit local changes are written back to the
// user record (see LanguageSelector), so this never fights a fresh selection.
function LanguageSync() {
  const { i18n: i18nInstance } = useTranslation();
  const user = useUserOptional();

  useEffect(() => {
    const preferred = user?.language;
    if (!preferred) return;
    const active = i18nInstance.language?.split("-")[0];
    if (preferred !== active) {
      void i18nInstance.changeLanguage(preferred);
    }
  }, [user?.language, i18nInstance]);

  return null;
}

export function I18nProvider({ children }: I18nProviderProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <LanguageSync />
      {children}
    </I18nextProvider>
  );
}
